import React, { useEffect, useRef, useState } from "react";
import { Modal, notification } from "antd";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress } from "@mui/material";
import { useDispatch } from "react-redux";
import { getSwmsEditHistory, getSwmsEditHistoryDetails } from "../../../../../../actions/v2/JobSwmsActions";
import styles from "../../../../SwmsMasters/SwmsMastersStyle.module.css";
import { getFlagLabel, getResponseTypeLabel } from "../../../../SwmsMasters/PreStartQuestions/preStartQuestionViewConfig";
import editHistoryStyles from "./EditHistoryModal.module.css";
import moment from "moment";

const getHistoryData = (response) => (response && response.data ? response.data : response || {});
const getDocumentTypeLabel = (documentType) => (
  documentType === "swms"
    ? "SWMS"
    : documentType || "Edit History"
);

const formatChangeCount = (count) => `${count} ${count === 1 ? "Change" : "Changes"}`;

const formatHistoryValue = (value, change = {}) => {
  if (value === null || value === undefined) return "Not set";
  if (value === "") return "Empty";
  if (value === false) return "False";
  if (value === true) return "True";
  if (change.field_name === "answer_scope") {
    if (value === "supervisor") return "Supervisor";
    if (value === "worker") return "Personal / Per Worker";
  }
  if (value && typeof value === "object" && !Array.isArray(value)) {
    if (value.label !== undefined) return String(value.label);
    if (value.method_name !== undefined) return String(value.method_name);
    if (value.value !== undefined) return String(value.value);
  }
  return String(value);
};

const formatToggleValue = (change, value) => {
  if (change && change.field_name === "active") {
    if (value === 1 || value === true || value === "1" || value === "true") return "Active";
    if (value === 0 || value === false || value === "0" || value === "false") return "Inactive";
  }
  if (change && change.field_name === "required") {
    if (value === 1 || value === true || value === "1" || value === "true") return "Required";
    if (value === 0 || value === false || value === "0" || value === "false") return "Optional";
  }
  if (value === 1 || value === true || value === "1" || value === "true") return "Enabled";
  if (value === 0 || value === false || value === "0" || value === "false") return "Disabled";
  return formatHistoryValue(value);
};

const isMethodItemToggleChange = (change) => (
  change.change_type === "multiple_items"
  && change.field_name === "method_items"
  && ["toggled_on", "toggled_off", "added", "deleted"].includes(change.action)
);

const getMethodItemNames = (change) => {
  const items = Array.isArray(change.new_value) && change.new_value.length > 0
    ? change.new_value
    : (Array.isArray(change.previous_value) ? change.previous_value : []);
  return items
    .map((item) => item && item.method_item)
    .filter(Boolean)
    .join(", ");
};

const getMethodItemUpdates = (change) => {
  const previousItems = Array.isArray(change.previous_value) ? change.previous_value : [];
  const newItems = Array.isArray(change.new_value) ? change.new_value : [];
  const previousById = new Map(previousItems.map((item) => [item && item.id, item]));

  return newItems
    .map((item) => {
      const previous = previousById.get(item && item.id);
      return previous && item && previous.method_item !== item.method_item
        ? { previous: previous.method_item, next: item.method_item }
        : null;
    })
    .filter(Boolean);
};

const getSectionOrderMoves = (change) => {
  const previousItems = Array.isArray(change.previous_value) ? change.previous_value : [];
  const newItems = Array.isArray(change.new_value) ? change.new_value : [];
  const newById = new Map(newItems.map((item) => [item && item.id, item]));

  return previousItems
    .map((previousItem) => {
      const nextItem = newById.get(previousItem && previousItem.id);
      if (!nextItem || previousItem.position === nextItem.position) {
        return null;
      }

      return {
        label: nextItem.label || previousItem.label || previousItem.id,
        previousPosition: previousItem.position,
        newPosition: nextItem.position
      };
    })
    .filter(Boolean);
};

const getQuestionOrderMoves = (change) => {
  const previousItems = Array.isArray(change.previous_value) ? change.previous_value : [];
  const newItems = Array.isArray(change.new_value) ? change.new_value : [];
  const newById = new Map(newItems.map((item) => [item && item.id, item]));

  return previousItems
    .map((previousItem) => {
      const nextItem = newById.get(previousItem && previousItem.id);
      if (!nextItem || previousItem.position === nextItem.position) {
        return null;
      }

      return {
        label: nextItem.label || previousItem.label || previousItem.id,
        previousPosition: previousItem.position,
        newPosition: nextItem.position
      };
    })
    .filter(Boolean);
};

const isAddedPreStartQuestion = (change, section) => (
  section
  && section.section_key === "pre_start_questions"
  && change
  && change.field_name === "question"
  && change.action === "added"
);

const AddedPreStartQuestion = ({ change }) => {
  const question = change.new_value || {};
  const answerScope = question.answer_scope === "worker" ? "Personal / Per Worker" : "Supervisor";
  const flagLabel = getFlagLabel(question.response_config);
  const questionDetails = [
    { label: "Question Text", value: question.question_text || "Untitled question" },
    question.guidance_text ? { label: "Guidance Text", value: question.guidance_text } : null,
    { label: "Answer Scope", value: answerScope },
    { label: "Response Type", value: getResponseTypeLabel(question.response_config) },
    { label: "Required", value: question.required ? "Required" : "Optional" },
    flagLabel ? { label: "Flag Value", value: flagLabel } : null,
    question.method_name ? { label: "Method", value: question.method_name } : null,
    { label: "Status", value: question.active ? "Active" : "Inactive" }
  ].filter(Boolean);

  return (
    <div className={`${editHistoryStyles.changeRow} ${editHistoryStyles.addedQuestionChangeRow}`.trim()}>
      <div className={editHistoryStyles.changeLabel}>New Question Added</div>
      <div className={editHistoryStyles.addedQuestionDetails}>
        {questionDetails.map((detail) => (
          <div className={editHistoryStyles.addedQuestionDetail} key={detail.label}>
            <span className={editHistoryStyles.addedQuestionDetailLabel}>{detail.label}</span>
            <svg className={editHistoryStyles.changeArrow} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="currentColor" aria-hidden="true">
              <path d="M566.6 342.6C579.1 330.1 579.1 309.8 566.6 297.3L406.6 137.3C394.1 124.8 373.8 124.8 361.3 137.3C348.8 149.8 348.8 170.1 361.3 182.6L466.7 288L96 288C78.3 288 64 302.3 64 320C64 337.7 78.3 352 96 352L466.7 352L361.3 457.4C348.8 469.9 348.8 490.2 361.3 502.7C373.8 515.2 394.1 515.2 406.6 502.7L566.6 342.7z" />
            </svg>
            <span className={editHistoryStyles.addedQuestionDetailValue}>{detail.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const EditHistoryChangeRow = ({ change, section }) => {
  if (isAddedPreStartQuestion(change, section)) {
    return <AddedPreStartQuestion change={change} />;
  }

  if (isMethodItemToggleChange(change)) {
    return (
      <div className={editHistoryStyles.changeRow}>
        <div className={editHistoryStyles.changeLabel}>{change.field_label || change.field_name || "Change"}</div>
        <div className={`${editHistoryStyles.changeValues} ${editHistoryStyles.methodItemSingleValue}`.trim()}>
          <span className={editHistoryStyles.newValue}>
            {change.action === "toggled_on"
              ? "Selected"
              : (change.action === "toggled_off"
                ? "Unselected"
                : (change.action === "added" ? "Added" : "Removed"))} - {getMethodItemNames(change)}
          </span>
        </div>
      </div>
    );
  }

  if (
    change.change_type === "multiple_items"
    && change.field_name === "method_items"
    && change.action === "updated"
  ) {
    const updates = getMethodItemUpdates(change);
    return (
      <div className={editHistoryStyles.changeRow}>
        <div className={editHistoryStyles.changeLabel}>{change.field_label || change.field_name || "Change"}</div>
        <div className={editHistoryStyles.methodItemUpdates}>
          {updates.map((update, index) => (
            <div className={editHistoryStyles.methodItemUpdate} key={`${update.previous}-${update.next}-${index}`}>
              <span className={editHistoryStyles.previousValue}>{update.previous}</span>
              <svg className={editHistoryStyles.changeArrow} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="currentColor" aria-hidden="true">
                <path d="M566.6 342.6C579.1 330.1 579.1 309.8 566.6 297.3L406.6 137.3C394.1 124.8 373.8 124.8 361.3 137.3C348.8 149.8 348.8 170.1 361.3 182.6L466.7 288L96 288C78.3 288 64 302.3 64 320C64 337.7 78.3 352 96 352L466.7 352L361.3 457.4C348.8 469.9 348.8 490.2 361.3 502.7C373.8 515.2 394.1 515.2 406.6 502.7L566.6 342.7z" />
              </svg>
              <span className={editHistoryStyles.newValue}>{update.next}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (
    change.change_type === "multiple_items"
    && change.field_name === "active_section_order"
    && change.action === "reordered"
  ) {
    const moves = getSectionOrderMoves(change);
    return (
      <div className={editHistoryStyles.changeRow}>
        <div className={editHistoryStyles.changeLabel}>{change.field_label || "Section Order"}</div>
        <div className={editHistoryStyles.sectionOrderMoves}>
          {moves.map((move, index) => (
            <div className={editHistoryStyles.sectionOrderMove} key={`${move.label}-${index}`}>
              <span>{move.label}</span>
              <span className={editHistoryStyles.sectionOrderPosition}>{move.previousPosition}</span>
              <svg className={editHistoryStyles.changeArrow} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="currentColor" aria-hidden="true">
                <path d="M566.6 342.6C579.1 330.1 579.1 309.8 566.6 297.3L406.6 137.3C394.1 124.8 373.8 124.8 361.3 137.3C348.8 149.8 348.8 170.1 361.3 182.6L466.7 288L96 288C78.3 288 64 302.3 64 320C64 337.7 78.3 352 96 352L466.7 352L361.3 457.4C348.8 469.9 348.8 490.2 361.3 502.7C373.8 515.2 394.2 515.2 406.6 502.7L566.6 342.7z" />
              </svg>
              <span className={editHistoryStyles.sectionOrderPosition}>{move.newPosition}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (
    change.change_type === "multiple_items"
    && change.field_name === "question_order"
    && change.action === "reordered"
  ) {
    const moves = getQuestionOrderMoves(change);
    return (
      <div className={editHistoryStyles.changeRow}>
        <div className={editHistoryStyles.changeLabel}>{change.field_label || "Question Order"}</div>
        <div className={editHistoryStyles.sectionOrderMoves}>
          {moves.map((move, index) => (
            <div className={editHistoryStyles.sectionOrderMove} key={`${move.label}-${index}`}>
              <span>{move.label}</span>
              <span className={editHistoryStyles.sectionOrderPosition}>{move.previousPosition}</span>
              <svg className={editHistoryStyles.changeArrow} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="currentColor" aria-hidden="true">
                <path d="M566.6 342.6C579.1 330.1 579.1 309.8 566.6 297.3L406.6 137.3C394.1 124.8 373.8 124.8 361.3 137.3C348.8 149.8 348.8 170.1 361.3 182.6L466.7 288L96 288C78.3 288 64 302.3 64 320C64 337.7 78.3 352 96 352L466.7 352L361.3 457.4C348.8 469.9 348.8 490.2 361.3 502.7C373.8 515.2 394.1 515.2 406.6 502.7L566.6 342.7z" />
              </svg>
              <span className={editHistoryStyles.sectionOrderPosition}>{move.newPosition}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (change.change_type === "rich_text") {
    return (
      <div className={editHistoryStyles.changeRow}>
        <div className={editHistoryStyles.changeLabel}>{change.field_label || change.field_name || "Change"}</div>
        <div className={editHistoryStyles.richTextValues}>
          <div className={editHistoryStyles.richTextPrevious}>
            <div dangerouslySetInnerHTML={{ __html: change.previous_value || "" }} />
          </div>
          <svg className={editHistoryStyles.changeArrow} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="currentColor" aria-hidden="true">
            <path d="M566.6 342.6C579.1 330.1 579.1 309.8 566.6 297.3L406.6 137.3C394.1 124.8 373.8 124.8 361.3 137.3C348.8 149.8 348.8 170.1 361.3 182.6L466.7 288L96 288C78.3 288 64 302.3 64 320C64 337.7 78.3 352 96 352L466.7 352L361.3 457.4C348.8 469.9 348.8 490.2 361.3 502.7C373.8 515.2 394.1 515.2 406.6 502.7L566.6 342.7z" />
          </svg>
          <div className={editHistoryStyles.richTextNew}>
            <div dangerouslySetInnerHTML={{ __html: change.new_value || "" }} />
          </div>
        </div>
      </div>
    );
  }

  const isToggle = change.action === "toggled_on" || change.action === "toggled_off";
  const previousValue = change.previous_value;
  const newValue = change.new_value;
  return (
    <div className={editHistoryStyles.changeRow}>
      <div className={editHistoryStyles.changeLabel}>{change.field_label || change.field_name || "Change"}</div>
      <div className={editHistoryStyles.changeValues}>
        <span className={editHistoryStyles.previousValue}>
          {isToggle ? formatToggleValue(change, previousValue) : formatHistoryValue(previousValue, change)}
        </span>
        <svg className={editHistoryStyles.changeArrow} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="currentColor">
          <path d="M566.6 342.6C579.1 330.1 579.1 309.8 566.6 297.3L406.6 137.3C394.1 124.8 373.8 124.8 361.3 137.3C348.8 149.8 348.8 170.1 361.3 182.6L466.7 288L96 288C78.3 288 64 302.3 64 320C64 337.7 78.3 352 96 352L466.7 352L361.3 457.4C348.8 469.9 348.8 490.2 361.3 502.7C373.8 515.2 394.1 515.2 406.6 502.7L566.6 342.7z" />
        </svg>
        <span className={editHistoryStyles.newValue}>
          {isToggle ? formatToggleValue(change, newValue) : formatHistoryValue(newValue, change)}
        </span>
      </div>
    </div>
  );
};

const EditHistorySectionGroup = ({ section, documentType }) => {
  const sectionLabel = section.section_label || section.section_key || "Section";
  const isPreStartQuestion = documentType === "pre_start"
    && typeof section.section_key === "string"
    && section.section_key.startsWith("question_");

  return (
    <div className={editHistoryStyles.sectionGroup}>
      <div className={editHistoryStyles.sectionHeader}>
        <span className={editHistoryStyles.sectionLabel}>
          {isPreStartQuestion ? (
            <>
              <span className={editHistoryStyles.questionSectionPrefix}>Question:</span>{" "}
              {sectionLabel}
            </>
          ) : sectionLabel}
        </span>
        <span className={editHistoryStyles.sectionCount}>{formatChangeCount(Array.isArray(section.changes) ? section.changes.length : 0)}</span>
      </div>
      <div className={editHistoryStyles.changeList}>
        {(Array.isArray(section.changes) ? section.changes : []).map((change, index) => (
          <EditHistoryChangeRow
            key={`${change.field_name || "change"}-${index}`}
            change={change}
            section={section}
          />
        ))}
      </div>
    </div>
  );
};

const EditHistoryItemGroup = ({ item, documentType, documentName, showDocumentLabel = true }) => {
  const sections = Array.isArray(item.section_changes) ? item.section_changes : [];
  const isDocumentLevel = item.item_id === null || item.item_id === undefined;
  const hierarchyLabel = !isDocumentLevel && item.item_label
    ? item.item_label
    : (documentType === "swms"
      ? "SWMS Document"
      : (documentType === "pre_start" ? documentName || "Pre-Start" : getDocumentTypeLabel(documentType)));

  return (
    <div className={`${editHistoryStyles.itemGroup} ${isDocumentLevel ? editHistoryStyles.documentItemGroup : ""}`.trim()}>
      {(!isDocumentLevel || showDocumentLabel) ? (
        <div className={editHistoryStyles.itemHeader}>
          <span className={editHistoryStyles.itemLabel}>{hierarchyLabel}</span>
        </div>
      ) : null}
      <div className={editHistoryStyles.sectionGroups}>
        {sections.map((section, index) => (
          <EditHistorySectionGroup
            key={`${section.section_key || "section"}-${index}`}
            section={section}
            documentType={documentType}
          />
        ))}
      </div>
    </div>
  );
};

const EditHistoryDetailsHierarchy = ({ details = {} }) => {
  const items = Array.isArray(details.items) ? details.items : [];

  if (items.length === 0) {
    return <div className={editHistoryStyles.detailsEmpty}>No change details are available.</div>;
  }

  return (
    <div className={editHistoryStyles.detailsHierarchy}>
      {items.map((item, index) => (
        <EditHistoryItemGroup
          key={`${item.item_id || "document"}-${index}`}
          item={item}
          documentType={details.document_type}
          documentName={details.document_name}
          showDocumentLabel={(details.document_type !== "swms" && details.document_type !== "pre_start") || index === 0}
        />
      ))}
    </div>
  );
};

export default function EditHistoryModal({ visible, onClose, jobId, swmsId, version }) {
  const dispatch = useDispatch();
  const requestRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState({});
  const [detailsLoading, setDetailsLoading] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);

  const loadHistory = (cursor = null, append = false) => {
    if (requestRef.current) {
      requestRef.current.abort();
    }
    const controller = new AbortController();
    requestRef.current = controller;
    append ? setLoadingMore(true) : setLoading(true);
    setError(null);
    dispatch(getSwmsEditHistory(jobId, swmsId, version, { signal: controller.signal }, cursor ? { cursor } : {}))
      .then((response) => {
        const data = getHistoryData(response);
        setEvents((current) => (append ? [...current, ...(data.events || [])] : (data.events || [])));
        setHasMore(Boolean(data.has_more));
        setNextCursor(data.next_cursor || null);
      })
      .catch((requestError) => {
        if (requestError && requestError.name === "AbortError") return;
        setError(requestError);
        notification.error({ message: "Unable to load Edit History", description: requestError });
      })
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  };

  useEffect(() => {
    if (!visible) {
      if (requestRef.current) requestRef.current.abort();
      setEvents([]);
      setNextCursor(null);
      setHasMore(false);
      setLoading(false);
      setLoadingMore(false);
      setError(null);
      setDetails({});
      setDetailsLoading({});
      setSelectedEvent(null);
      return undefined;
    }

    setEvents([]);
    setNextCursor(null);
    setHasMore(false);
    setError(null);
    setDetails({});
    setDetailsLoading({});
    setSelectedEvent(null);
    loadHistory();

    return () => {
      if (requestRef.current) requestRef.current.abort();
    };
  }, [visible, jobId, swmsId, version]);

  const openDetails = (event) => {
    setSelectedEvent(event);
    if (details[event.event_id]) {
      return;
    }
    setDetailsLoading((current) => ({ ...current, [event.event_id]: true }));
    dispatch(getSwmsEditHistoryDetails(jobId, swmsId, event.event_id, version))
      .then((response) => setDetails((current) => ({ ...current, [event.event_id]: getHistoryData(response) })))
      .catch((requestError) => notification.error({ message: "Unable to load Edit History details", description: requestError }))
      .finally(() => setDetailsLoading((current) => ({ ...current, [event.event_id]: false })));
  };

  const renderEventRow = (event) => (
    <div
      key={event.event_id}
      className={editHistoryStyles.eventRow}
    >
      <div className={editHistoryStyles.eventContent}>
        <div className={editHistoryStyles.documentLine}>
          {event.document_type === "pre_start"
            ? (event.document_name || getDocumentTypeLabel(event.document_type))
            : getDocumentTypeLabel(event.document_type)}
          {event.document_type !== "pre_start" && event.document_name ? (
            <>
              {" "}
              <svg className={editHistoryStyles.breadcrumbChevron} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
                <path fill="currentColor" stroke="currentColor" strokeWidth="15px" strokeLinejoin="round" d="M439.1 297.4C451.6 309.9 451.6 330.2 439.1 342.7L279.1 502.7C266.6 515.2 246.3 515.2 233.8 502.7C221.3 490.2 221.3 469.9 233.8 457.4L371.2 320L233.9 182.6C221.4 170.1 221.4 149.8 233.9 137.3C246.4 124.8 266.7 124.8 279.2 137.3L439.2 297.3z" />
              </svg>
              {" "}
              {event.document_name}
            </>
          ) : ""}
        </div>
        <div className={editHistoryStyles.metadataRow}>
          <span className={editHistoryStyles.metadataText}>
            {event.modified_by_name || event.user_name || "Unknown user"}
          </span>
          <span className={editHistoryStyles.metadataSeparator}>
            ·
          </span>
          <span className={editHistoryStyles.metadataText}>
            {event.modified_at ? moment.utc(event.modified_at).format("DD/MM/YYYY hh:mm A") : "—"}
          </span>
          <span className={editHistoryStyles.metadataSeparator}>
            ·
          </span>
          <span className={editHistoryStyles.metadataText}>
            {event.change_count || 0} {event.change_count === 1 ? "Change" : "Changes"}
          </span>
        </div>
      </div>
      <button
        type="button"
        aria-label={`View edit details for ${event.document_name || event.document_type || "event"}`}
        onClick={() => openDetails(event)}
        className={editHistoryStyles.actionButton}
      >
        <svg className={editHistoryStyles.actionArrow} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="currentColor">
          <path d="M566.6 342.6C579.1 330.1 579.1 309.8 566.6 297.3L406.6 137.3C394.1 124.8 373.8 124.8 361.3 137.3C348.8 149.8 348.8 170.1 361.3 182.6L466.7 288L96 288C78.3 288 64 302.3 64 320C64 337.7 78.3 352 96 352L466.7 352L361.3 457.4C348.8 469.9 348.8 490.2 361.3 502.7C373.8 515.2 394.1 515.2 406.6 502.7L566.6 342.7z" />
        </svg>
      </button>
    </div>
  );

  return (
    <Modal
      className={`cs-modal-v2 ${editHistoryStyles.editHistoryModal}`}
      centered
      footer={null}
      visible={visible}
      onCancel={onClose}
      maskClosable
      destroyOnClose
      width={900}
      bodyStyle={{ paddingRight: "0px", paddingLeft: "0px" }}
      closeIcon={<div className={styles.ModalCloseIcon}><CloseIcon /></div>}
    >
      <div className="v2-modal-container" style={{ color: "#212529" }}>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className={styles.ModalHeading}>Edit History</div>
              <hr className="v3-modal-hr" />
              {loading ? <div className="text-center"><CircularProgress size={24} /></div> : null}
              {!loading && error ? <div className="text-danger">{error}</div> : null}
              {!loading && !error && selectedEvent ? (
                <div className={editHistoryStyles.detailsTopBar}>
                  <button type="button" className={editHistoryStyles.backButton} onClick={() => setSelectedEvent(null)}>
                    <svg className={editHistoryStyles.backIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
                      <path fill="currentColor" d="M73.4 297.4C60.9 309.9 60.9 330.2 73.4 342.7L233.4 502.7C245.9 515.2 266.2 515.2 278.7 502.7C291.2 490.2 291.2 469.9 278.7 457.4L173.3 352L544 352C561.7 352 576 337.7 576 320C576 302.3 561.7 288 544 288L173.3 288L278.7 182.6C291.2 170.1 291.2 149.8 278.7 137.3C266.2 124.8 245.9 124.8 233.4 137.3L73.4 297.3z" />
                    </svg>
                    Back
                  </button>
                  <div className={editHistoryStyles.detailsDocument}>
                    {selectedEvent.document_type === "pre_start"
                      ? (selectedEvent.document_name || getDocumentTypeLabel(selectedEvent.document_type))
                      : `${getDocumentTypeLabel(selectedEvent.document_type)}${selectedEvent.document_name ? ` › ${selectedEvent.document_name}` : ""}`}
                  </div>
                  <div className={editHistoryStyles.detailsMetadata}>
                    <span>{selectedEvent.modified_by_name || selectedEvent.user_name || "Unknown user"}</span>
                    <span>·</span>
                    <span>{selectedEvent.modified_at ? moment.utc(selectedEvent.modified_at).format("DD/MM/YYYY hh:mm A") : "—"}</span>
                    <span>·</span>
                    <span>{selectedEvent.change_count || 0} {selectedEvent.change_count === 1 ? "Change" : "Changes"}</span>
                  </div>
                </div>
              ) : null}
              {!loading && !error && selectedEvent && detailsLoading[selectedEvent.event_id] ? (
                <div className="text-center mt-3"><CircularProgress size={20} /></div>
              ) : null}
              {!loading && !error && selectedEvent && !detailsLoading[selectedEvent.event_id] && details[selectedEvent.event_id] ? (
                <EditHistoryDetailsHierarchy details={details[selectedEvent.event_id]} />
              ) : null}
              {!loading && !error && !selectedEvent && events.length === 0 ? <div>No edit history is available.</div> : null}
              {!loading && !error && !selectedEvent ? events.map(renderEventRow) : null}
              {!selectedEvent && hasMore ? (
                <div className={editHistoryStyles.paginationAction}>
                  <button
                    type="button"
                    className={editHistoryStyles.loadMoreButton}
                    disabled={loadingMore}
                    onClick={() => loadHistory(nextCursor, true)}
                  >
                    {loadingMore ? <CircularProgress size={16} /> : null}
                    <span>{loadingMore ? "Loading..." : "Load more"}</span>
                  </button>
                </div>
              ) : null}
              {!selectedEvent && !loading && !error && events.length > 0 && !hasMore ? (
                <div className={editHistoryStyles.endOfHistory}>End of History</div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
